import { hasStoredFile } from '../../utils/artefactEvidence.js';
import { effectivenessStaleAfterMs } from '../../constants/effectivenessScale.js';
// Predicted-State Forecast over evidence nodes (A3)
//
// A PURE, read-time dimension on top of A1's evidence nodes (evidenceNodes.js).
// Given an audit date, it walks each node's OWN freshness clock and answers a
// single forward-looking question:
//
//   "This node is green today — will it still be green when the auditor arrives?"
//
// The reasoning advantage over evidence collection is exactly this: the argument
// shows its future weak links while there is still time to repair them. A node
// that is green now but whose freshness lapses on or before the audit date is
// flagged 'decays-before-audit', with the exact lapse date and the number of
// days of fix-window still remaining.
//
// ── Reuse, not a parallel engine ──────────────────────────────────────────────
// This does NOT re-implement forecasting. It reuses the SAME freshness semantics
// the live gates and the server sweepers already use, one clock per node kind:
//
//   • approved-document        → doc.nextReview                    (Gate 2 review SLA)
//   • satisfied-risk-obligation→ lastSatisfiedAt + cadence + grace  (isObligationSatisfied
//                                                                    in evaluateControl.js)
//   • verified-artefact        → (external-URL only) lastVerified +
//                                verificationIntervalDays           (externalUrlSweeper.js)
//   • effectiveness-rating     → lastLookedAt + review period       (§9.1 review clock,
//                                                                    effectivenessScale.js)
//   • completed-risk-action    → no freshness clock (a recorded historical fact)
//   • fulfilled-obligation     → no clock at node level (fulfilment is a rollup of
//                                underlying evidence — OBL-B-002 — which the node
//                                cannot see, so we never claim a decay we can't prove)
//
// The system carries other freshness sources — supplierEvidence.validUntil and
// risk-decision ttlDays — but no A1 node kind surfaces those directly today, so
// they are intentionally out of scope here (collectExpiringItems() already lists
// evidence expiry for the audit-prep view). When a node kind for them is added,
// give it a clock in nodeDecayInstant() below.
//
// ── Honesty rule (matches A2) ─────────────────────────────────────────────────
// A node with no determinable clock is 'no-clock', never silently 'fresh'. A node
// already lapsed as of today is 'observed-fail' (a current finding), never a
// forecast event — you cannot "predict" something that has already happened.
//
// Pure and read-time: no state keys, no persistence, no side effects.

const DAY_MS = 24 * 60 * 60 * 1000;

// Matches azure-functions/shared/artefacts/externalUrlSweeper.js and
// src/state/initialState.js (artefactSettings.verificationIntervalDays).
const DEFAULT_VERIFICATION_INTERVAL_DAYS = 90;

export const PREDICTED_STATUS = Object.freeze({
  // Fresh today and still fresh at (and beyond) the audit date.
  FRESH_THROUGH_AUDIT: 'fresh-through-audit',
  // Green today, but its freshness clock lapses on or before the audit date.
  DECAYS_BEFORE_AUDIT: 'decays-before-audit',
  // Already lapsed as of today — a current finding, not a forecast.
  OBSERVED_FAIL: 'observed-fail',
  // No determinable freshness clock — nothing to forecast (never over-claimed).
  NO_CLOCK: 'no-clock',
});

/** Parse a date-ish value (ms | Date | ISO) to ms, or NaN. */
function ms(v) {
  if (v == null || v === '') return NaN;
  if (typeof v === 'number') return v;
  return new Date(v).getTime();
}

/** Resolve an instant to ms, falling back to `fallback` on null/invalid input. */
function resolveInstant(v, fallback) {
  const m = ms(v);
  return Number.isFinite(m) ? m : fallback;
}

/** Whole days from `fromMs` to `toMs`, floored (matches forecastControl.js). */
function daysUntil(fromMs, toMs) {
  return Math.floor((toMs - fromMs) / DAY_MS);
}

/**
 * An artefact is blob-backed (and so never decays on the external-URL clock).
 *
 * `filename` used to qualify here, which exempted every file-less artefact
 * from the decay clock — a row whose bytes were discarded in the browser was
 * forecast as permanently fresh. Only a server-minted ref qualifies now
 * (src/utils/artefactEvidence.js, ARTF-B-002).
 */
function isBlobBacked(artefact) {
  return hasStoredFile(artefact);
}

function verificationIntervalDays(sources) {
  const raw = sources?.artefactSettings?.verificationIntervalDays;
  return Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_VERIFICATION_INTERVAL_DAYS;
}

/**
 * The instant a node's freshness lapses — i.e. the LAST instant at which it is
 * still fresh. Returns NaN when the node kind has no determinable freshness clock
 * (a historical fact, or a record whose freshness field is absent).
 *
 * Semantics are uniform across kinds: the node is fresh while `now <= decayMs`
 * and lapses once `now > decayMs`, exactly matching the strict-overdue check in
 * evaluateControl (overdueAsOf: due < asOf) and the inclusive cadence window in
 * isObligationSatisfied (now <= satisfied + window).
 *
 * @param {object} node     an A1 node from selectEvidenceNodes()
 * @param {object} sources  { documents, artefacts, riskObligations, artefactSettings }
 * @returns {number} ms epoch of the lapse instant, or NaN for no clock
 */
export function nodeDecayInstant(node, sources) {
  if (!node) return NaN;
  const refs = node.backingRefs || {};
  const s = sources || {};

  switch (node.kind) {
    case 'approved-document': {
      // Review SLA (Gate 2): a published+approved document stays fresh until its
      // nextReview date. A document with no nextReview has no review clock.
      const doc = (s.documents || []).find((d) => d && d.id === refs.docId);
      return ms(doc?.nextReview);
    }

    case 'satisfied-risk-obligation': {
      // Cadence window (Gate 4): fresh while now <= lastSatisfiedAt + cadence + grace.
      // Mirrors isObligationSatisfied() — when cadence/grace are not both finite,
      // the obligation never ages out, so there is no clock to forecast.
      const ob = (s.riskObligations || []).find((o) => o && o.id === refs.obligationId);
      const satisfiedMs = ms(ob?.lastSatisfiedAt ?? refs.lastSatisfiedAt);
      if (!Number.isFinite(satisfiedMs)) return NaN;
      if (!ob || !Number.isFinite(ob.cadenceDays) || !Number.isFinite(ob.graceDays)) return NaN;
      return satisfiedMs + (ob.cadenceDays + ob.graceDays) * DAY_MS;
    }

    case 'verified-artefact': {
      // External-URL artefacts flip to 'unverified' once lastVerified is older than
      // verificationIntervalDays (externalUrlSweeper.js). Blob-backed artefacts are
      // primary evidence and never decay on this clock (ARTF-B-002).
      const art = (s.artefacts || []).find((a) => a && a.id === refs.artefactId);
      if (!art) return NaN;
      if (isBlobBacked(art)) return NaN;
      if (!art.externalUrl) return NaN;
      const lastVerifiedMs = ms(art.lastVerified);
      if (!Number.isFinite(lastVerifiedMs)) return NaN;
      return lastVerifiedMs + verificationIntervalDays(s) * DAY_MS;
    }

    case 'effectiveness-rating': {
      // §9.1 review clock (GH #7168): fresh while the least recently looked-at
      // rating is inside its review period (effectivenessScale.js — the same
      // rule the maturity screen's stale flag uses). No readable date → no
      // clock here; the node itself already carries LOW confidence for that.
      return effectivenessStaleAfterMs({ ratedAt: refs.lastLookedAt });
    }

    // A completed risk action is a recorded historical fact; a fulfilled catalogue
    // obligation's currency is a rollup the node level cannot see. Neither exposes
    // a freshness clock we can honestly forecast from.
    case 'completed-risk-action':
    case 'fulfilled-obligation':
    default:
      return NaN;
  }
}

/**
 * Classify a node's predicted state between `now` and the audit date.
 *
 * @param {object} node                 an A1 node from selectEvidenceNodes()
 * @param {object} sources              source records (see nodeDecayInstant)
 * @param {number|Date|string} nowInput the "green now" reference instant (default: now)
 * @param {number|Date|string} auditInput the audit date (default: nowInput)
 * @returns {{ status: string, decayDate: string|null, fixWindowDays: number|null }}
 */
export function predictNodeDecay(node, sources, nowInput, auditInput) {
  const nowMs = resolveInstant(nowInput, Date.now());
  const auditMs = resolveInstant(auditInput, nowMs);
  const decayMs = nodeDecayInstant(node, sources);

  if (!Number.isFinite(decayMs)) {
    return { status: PREDICTED_STATUS.NO_CLOCK, decayDate: null, fixWindowDays: null };
  }

  const decayDate = new Date(decayMs).toISOString();

  // Already lapsed as of today — a current finding, not a forecast.
  if (decayMs < nowMs) {
    return { status: PREDICTED_STATUS.OBSERVED_FAIL, decayDate, fixWindowDays: null };
  }

  // Green now, but the clock lapses on or before the audit date. The inclusive
  // upper bound / exclusive lower bound matches collectExpiringItems() exactly.
  if (decayMs <= auditMs) {
    return {
      status: PREDICTED_STATUS.DECAYS_BEFORE_AUDIT,
      decayDate,
      fixWindowDays: daysUntil(nowMs, decayMs),
    };
  }

  // Fresh through the audit date (and beyond).
  return {
    status: PREDICTED_STATUS.FRESH_THROUGH_AUDIT,
    decayDate,
    fixWindowDays: daysUntil(nowMs, decayMs),
  };
}
